import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Theme = 'dark' | 'light' | 'system'
type ResolvedTheme = 'dark' | 'light'

interface ThemeState {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
  initTheme: () => void
}

/**
 * Store de tema (dark/light/system) com persistencia em localStorage.
 * A classe `.dark` no <html> e o que controla o darkMode:'class' do Tailwind.
 *
 * Atencao: o script anti-flash em public/index.html aplica a classe ANTES
 * do React montar, pra evitar flash do tema errado na primeira carga.
 * Este store cuida apenas das mudancas posteriores via toggle.
 */
const resolveSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const applyThemeClass = (resolved: ResolvedTheme) => {
  if (typeof document === 'undefined') return
  const html = document.documentElement
  if (resolved === 'dark') {
    html.classList.add('dark')
  } else {
    html.classList.remove('dark')
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      resolvedTheme: 'dark',

      setTheme: (theme) => {
        const resolved: ResolvedTheme = theme === 'system' ? resolveSystemTheme() : theme
        applyThemeClass(resolved)
        set({ theme, resolvedTheme: resolved })
      },

      // Reaplica o tema no load — util pra sincronizar o DOM com o valor do store
      initTheme: () => {
        const { theme } = get()
        const resolved: ResolvedTheme = theme === 'system' ? resolveSystemTheme() : theme
        applyThemeClass(resolved)
        set({ resolvedTheme: resolved })
      },
    }),
    {
      name: 'vision-theme',
      // So persiste theme — resolvedTheme e derivado e sera recalculado no init
      partialize: (state) => ({ theme: state.theme }),
    }
  )
)
